import { Button } from '@eventbrite/eds-button';
import { HAS_DOCUMENT } from '@eventbrite/eds-utils';
import { HAS_WINDOW } from '@eventbrite/feature-detection';
import { GenericLazyString, gettext } from '@eventbrite/i18n';
import React, { useState } from 'react';
import { FeatureFlags } from '../../../../types';
import { ScrollableDrawer } from '../../../ScrollableDrawer/ScrollableDrawer';
import { Modal } from '../../Modal';
import { CreatorAvatar } from './CreatorAvatar';
import './CreatorAvatarBadge.scss';

interface CreatorAvatarBadgeProps {
    organizerId: string;
    organizerAvatar?: string;
    featureFlags?: FeatureFlags;
    showBadge?: boolean;
    isMobile?: boolean;
}

export const CreatorAvatarBadge = ({
    organizerId,
    organizerAvatar,
    featureFlags,
    showBadge,
    isMobile,
}: CreatorAvatarBadgeProps) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const onClickOutside = () => {
        setIsModalOpen(false);
    };

    return (
        <>
            <CreatorAvatar
                organizerId={organizerId}
                organizerAvatar={organizerAvatar}
                featureFlags={featureFlags}
                showBadge={showBadge}
                setIsModalOpen={setIsModalOpen}
            />

            {isModalOpen && (
                <>
                    {isMobile ? (
                        <ScrollableDrawer
                            onClickOutside={onClickOutside}
                            heightFixed="307px"
                            isScrollUpAllowed={false}
                        >
                            <BadgeScrollableDrawerContent
                                onClickOutside={onClickOutside}
                                creatorAvatar={
                                    <CreatorAvatar
                                        organizerId={organizerId}
                                        organizerAvatar={organizerAvatar}
                                        featureFlags={featureFlags}
                                        showBadge={showBadge}
                                    />
                                }
                                heading={gettext('This organizer is verified!')}
                                content={gettext(
                                    "We've met these event organizers and confirmed their identity. They bring you unique things to do on Eventbrite.",
                                )}
                            />
                        </ScrollableDrawer>
                    ) : (
                        <Modal
                            visible={isModalOpen}
                            noPadding={true}
                            type="simple"
                            __containerClassName="organizer-info-avatar-badge-modal"
                            onClose={() => setIsModalOpen(false)}
                        >
                            <BadgeScrollableDrawerContent
                                onClickOutside={onClickOutside}
                                creatorAvatar={
                                    <CreatorAvatar
                                        organizerId={organizerId}
                                        organizerAvatar={organizerAvatar}
                                        featureFlags={featureFlags}
                                        showBadge={showBadge}
                                    />
                                }
                                heading={gettext('This organizer is verified!')}
                                content={gettext(
                                    "We've met these event organizers and confirmed their identity. They bring you unique things to do on Eventbrite.",
                                )}
                            />
                        </Modal>
                    )}
                </>
            )}
        </>
    );
};

interface BadgeScrollableDrawerContentProps {
    onClickOutside: () => void;
    creatorAvatar?: JSX.Element;
    heading: GenericLazyString;
    content: GenericLazyString;
}

export const BadgeScrollableDrawerContent = ({
    onClickOutside,
    creatorAvatar,
    heading,
    content,
}: BadgeScrollableDrawerContentProps) => {
    if (!HAS_WINDOW || !HAS_DOCUMENT) {
        return null;
    }

    return (
        <div
            className="organizer-info-creator-avatar-badge-content"
            data-testid="organizer-info-avatar-badge-content"
        >
            {creatorAvatar && (
                <div className="organizer-info-avatar">{creatorAvatar}</div>
            )}
            <div className="creator-badge-scroll-heading">{heading}</div>
            <div className="creator-badge-scroll-content">{content}</div>
            <Button onClick={onClickOutside}>Got it</Button>
        </div>
    );
};
